import { StepInfo } from './step-info';

export class FormSteps {
  private steps: StepInfo[] = [];
  public activeIndex: number = 0;
  private startingIndex: number = 0;

  public get length(): number {
    return this.steps.length;
  }

  public getActiveStep(): StepInfo {
    return this.steps[this.activeIndex];
  }

  public getStepByIndex(index?: number): StepInfo | undefined {
    if (index === undefined || index === null) return undefined;
    if (index >= 0 && index < this.steps.length) {
      return this.steps[index];
    }
    return undefined;
  }

  public getStepByID(ID: string): StepInfo {
    return this.steps.find((s) => s.ID === ID);
  }

  public push(step: StepInfo) {
    if (this.steps.some((s) => s.ID === step.ID)) {
      return;
    }
    this.steps.push(step);
  }

  public removeStep(stepId: string) {
    this.steps = this.steps.filter((s) => s.ID !== stepId);
  }

  public getLength(): number {
    return this.steps.length;
  }

  public isLastStep(index?: number): boolean {
    const stepIndex = index ?? this.activeIndex;
    return stepIndex === this.steps.length - 1;
  }

  public setStartingIndex(index: number): void {
    this.startingIndex = index;
    this.activeIndex = Math.max(index, this.activeIndex);
  }

  public getStartingIndex(): number {
    return this.startingIndex;
  }

  public isFirstStep(index?: number): boolean {
    const stepIndex = index ?? this.activeIndex;
    return stepIndex === this.startingIndex;
  }

  public stepIsNotActiveStep(nextIndex?: number, ID?: string): boolean {
    if (this.activeIndex === nextIndex) {
      return false;
    }
    if (this.getActiveStep().ID === ID) {
      return false;
    }
    return true;
  }

  /**
   * Returns the next step in the array, or null if there isn't one.
   */
  public getNextStep(): StepInfo | null {
    if (this.isLastStep()) {
      return null;
    }
    const nextIndex = this.activeIndex + 1;
    return this.steps[nextIndex];
  }

  /**
   * Returns the next non-disabled stepInfo in the array
   */
  public getNextEnabledStep(): StepInfo | null {
    if (this.isLastStep()) {
      return null;
    }
    let nextIndex = this.activeIndex + 1;
    while (this.steps[nextIndex].isDisabled && !this.isLastStep(nextIndex)) {
      nextIndex++;
    }
    return this.steps[nextIndex];
  }

  public getPreviousValidIndex(): number {
    if (!this.isFirstStep()) {
      let previousIndex: number = this.activeIndex;
      while(this.getStartingIndex() < previousIndex && previousIndex > 0 ){   
        previousIndex--;
        if(this.getStepByIndex(previousIndex).enabled){
          break;
        }
      }
      return previousIndex;
    }
    return this.activeIndex;
  }
  

  public enableStepByIndex(index: number): void {
    const step = this.getStepByIndex(index);
    if (step){
      step.enabled = true;
    }
  }
  public enableStepByID(ID: string): void {
    const step = this.getStepByID(ID);
    if (step){
      step.enabled = true;
    }
  }
  public disableStepByIndex(index: number): void {
    const step = this.getStepByIndex(index);
    if (step){
      step.enabled = false;
    }
  }
  public disableStepByID(ID: string): void {
    const step = this.getStepByID(ID);
    if (step){
      step.enabled = false;
    }
  }

  /** 
   * Moves current form "steps" to the next non disabled step.
   * If logic should fire to check for step enablement, fire it first.
   * */
  public iterateStep(options?: { reverse?: boolean }): void {
    const nextValidIndex: number = this.nextValidIndex(options?.reverse);
    this.activeIndex = nextValidIndex;
  }

  /**
   * Finds the next valid (non disabled) step index.
   */
  public nextValidIndex(reverse?: boolean): number {
    const iterator: number = reverse ? -1 : 1;
    let newIndex = this.activeIndex + iterator;
    while (this.steps[newIndex].isDisabled && newIndex < this.steps.length && newIndex >= 0) {
      newIndex += iterator;
    }
    return newIndex;
  }

  constructor(init?: Partial<StepInfo>) {
    Object.assign(this, init);
  }
}
